import { Box, Divider, Stack, Typography } from "@mui/material";
import { Empty, Flex, Loader } from "@zfp-akademie-a-s/zfp-design";
import { ErrorIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import {
  useClientDetail,
  useGetAnalysis,
  useGetAnalysisGoals,
  useListContractProposals,
  useListCurrentContracts,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  ContractProposalBaseInvestmentToolType,
  ContractProposalBaseSolutionHorizonType,
  type ContractProposalListItem,
  CurrentContractBaseSolutionHorizonType,
  type CurrentContractListItem,
  GoalOverviewHorizon,
  NsurePlanOverviewHorizon,
} from "Infrastructure/ApiZfp/Schema";
import { useResponsive } from "Infrastructure/Hooks/useResponsive";
import { Spacing, TABLET_MAX_WIDTH } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { CLIENT_DETAIL_RETRY, selectClientDetailClient } from "Infrastructure/Utils/ClientUtils";
import { type FC, startTransition, useContext, useState } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "../_Shared/InvestmentCaseContext";
import { useLockedInvestmentCaseStatus } from "../_Shared/useLockedInvestmentCaseStatus";
import { CurrentSolutionDrawer } from "./CurrentSolutionDrawer";
import { GoalFulfillmentAlerts } from "./GoalFulfillmentAlerts";
import { GoalFulfillmentContractingButton } from "./GoalFulfillmentContractingButton";
import { GoalsFulfillmentProvider } from "./GoalsFulfillmentContext";
import { GoalsFulfillmentContractingPartners } from "./GoalsFulfillmentContractingPartners";
import { GoalFulfillmentSettings } from "./GoalsFulfillmentSettings";
import { GoalsFulfillmentSidebarWrapper } from "./GoalsFulfillmentSidebar";
import { GoalsFulfillmentTerm } from "./GoalsFulfillmentTerm";
import { ProposedSolutionDrawer } from "./ProposedSolutionDrawer";
import { useGoalFulfillmentAlerts } from "./useGoalFulfillmentAlerts";

const SOLUTION_TO_NSURE_HORIZON: Record<ContractProposalBaseSolutionHorizonType, NsurePlanOverviewHorizon> = {
  [ContractProposalBaseSolutionHorizonType.short_term]: NsurePlanOverviewHorizon.short,
  [ContractProposalBaseSolutionHorizonType.mid_term]: NsurePlanOverviewHorizon.middle,
  [ContractProposalBaseSolutionHorizonType.long_term]: NsurePlanOverviewHorizon.long,
};

export const GoalsFulfillmentPane: FC = () => {
  const { t } = useResource();
  const isTablet = useResponsive(TABLET_MAX_WIDTH);

  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { data: analysis } = useGetAnalysis(analysisId, versionId);
  const { data: client } = useClientDetail(analysis?.clientGuid ?? "", {
    query: { enabled: !!analysis?.clientGuid, select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY },
  });

  const { hasAlerts } = useGoalFulfillmentAlerts({ client });
  const { data: goalsData, isFetched } = useGetAnalysisGoals(
    analysisId,
    versionId,
    { lifecycleStatus: "active", progressStatus: "in_progress" },
    { query: { staleTime: 0, gcTime: 0 } },
  );

  const {
    data: contractProposals,
    isPending: isContractProposalsPending,
    isError: isContractProposalsError,
  } = useListContractProposals(analysisId, versionId);
  const {
    data: currentContracts,
    isPending: isCurrentContractsPending,
    isError: isCurrentContractsError,
  } = useListCurrentContracts(analysisId, versionId);

  const { lockedCaseStatus, isInvestmentCaseLocked, isInvestmentStatesPending } = useLockedInvestmentCaseStatus(analysisId, versionId);

  const [currentSolutionDrawerOpen, setCurrentSolutionDrawerOpen] = useState(false);
  const [proposedSolutionDrawerOpen, setProposedSolutionDrawerOpen] = useState(false);
  const [currentSolutionHorizonType, setCurrentSolutionHorizonType] = useState<ContractProposalBaseSolutionHorizonType>(
    CurrentContractBaseSolutionHorizonType.short_term,
  );
  const [proposedSolutionHorizonType, setProposedSolutionHorizonType] = useState<ContractProposalBaseSolutionHorizonType>(
    ContractProposalBaseSolutionHorizonType.short_term,
  );
  const goals = goalsData?.goals ?? [];
  const hasNsureContractableProposal = (contractProposals?.items ?? []).some((item) => item.portfolio !== null);

  if (isContractProposalsPending || isCurrentContractsPending || isInvestmentStatesPending) {
    return <Loader />;
  }

  if (isContractProposalsError || isCurrentContractsError) {
    return (
      <Empty
        title={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Title)}
        text={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Text)}
        iconSize={40}
        icon={<ErrorIcon sx={{ color: "grey.400" }} />}
      />
    );
  }

  const handleCurrentSolutionDrawerOpen = (horizonType: ContractProposalBaseSolutionHorizonType) => {
    startTransition(() => {
      setCurrentSolutionHorizonType(horizonType);
      setCurrentSolutionDrawerOpen(true);
    });
  };

  const handleCurrentSolutionDrawerClose = () => {
    setCurrentSolutionDrawerOpen(false);
  };

  const handleProposedSolutionDrawerOpen = (horizonType: ContractProposalBaseSolutionHorizonType) => {
    startTransition(() => {
      setProposedSolutionHorizonType(horizonType);
      setProposedSolutionDrawerOpen(true);
    });
  };

  const handleProposedSolutionDrawerClose = () => {
    setProposedSolutionDrawerOpen(false);
  };

  const getNsurePlanByHorizon = (horizon: ContractProposalBaseSolutionHorizonType) =>
    goalsData?.nsurePlans?.find((plan) => plan.horizon === SOLUTION_TO_NSURE_HORIZON[horizon]);

  const getContractProposalsByHorizon = (horizon: ContractProposalBaseSolutionHorizonType): ContractProposalListItem[] => {
    if (!contractProposals) return [];
    return contractProposals.items.filter((item) => item.horizonType === horizon);
  };

  const getCurrentContractsByHorizon = (horizon: CurrentContractBaseSolutionHorizonType): CurrentContractListItem[] => {
    if (!currentContracts) return [];
    return currentContracts.items.filter((item) => item.horizonType === horizon);
  };

  const getFutureAmountByHorizon = (horizon: ContractProposalBaseSolutionHorizonType): number => {
    switch (horizon) {
      case ContractProposalBaseSolutionHorizonType.short_term:
        return (
          (contractProposals?.summary.shortTerm.futureAmount.value ?? 0) + (currentContracts?.summary.shortTerm.futureAmount.value ?? 0)
        );
      case ContractProposalBaseSolutionHorizonType.mid_term:
        return (contractProposals?.summary.midTerm.futureAmount.value ?? 0) + (currentContracts?.summary.midTerm.futureAmount.value ?? 0);
      case ContractProposalBaseSolutionHorizonType.long_term:
        return (contractProposals?.summary.longTerm.futureAmount.value ?? 0) + (currentContracts?.summary.longTerm.futureAmount.value ?? 0);
      default:
        return 0;
    }
  };

  return (
    <InvestmentCaseContext.Provider value={{ lockedCaseStatus, isInvestmentCaseLocked }}>
      <GoalsFulfillmentProvider clientGuid={client?.guid}>
        <Flex width={"100%"} justifyContent={"space-between"} alignItems={"center"} pr={Spacing.L}>
          <Box>
            <Typography variant="h3">{t(Resources.AnalysisDetail.GoalsFulfillment.Title)}</Typography>
            <Typography variant="body2" color="secondary">
              {t(Resources.AnalysisDetail.GoalsFulfillment.Subtitle)}
            </Typography>
          </Box>
          <Flex alignItems={"center"} gap={Spacing.L}>
            <GoalFulfillmentSettings />
            <Divider orientation="vertical" flexItem sx={{ mx: Spacing.M }} />
            <GoalsFulfillmentContractingPartners />
            {!isInvestmentCaseLocked && hasNsureContractableProposal && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: Spacing.M }} />
                <GoalFulfillmentContractingButton disabled={hasAlerts} />
              </>
            )}
          </Flex>
        </Flex>
        <Stack direction={isTablet ? "column" : "row"} sx={{ marginBottom: "120px !important" }}>
          <Stack sx={{ flex: 4 }} spacing={Spacing.XXL}>
            {<GoalFulfillmentAlerts client={client} />}
            {isFetched ? (
              <Box sx={{ pr: Spacing.XXL }}>
                <GoalsFulfillmentTerm
                  horizonType={ContractProposalBaseSolutionHorizonType.short_term}
                  goals={goals?.filter((item) => item.horizon === GoalOverviewHorizon.short)}
                  contractProposals={getContractProposalsByHorizon(ContractProposalBaseSolutionHorizonType.short_term)}
                  currentContracts={getCurrentContractsByHorizon(CurrentContractBaseSolutionHorizonType.short_term)}
                  futureAmount={getFutureAmountByHorizon(ContractProposalBaseSolutionHorizonType.short_term)}
                  handleCurrentSolutionDrawerOpen={handleCurrentSolutionDrawerOpen}
                  handleProposedSolutionDrawerOpen={handleProposedSolutionDrawerOpen}
                  nsurePlan={getNsurePlanByHorizon(ContractProposalBaseSolutionHorizonType.short_term)}
                />
                <GoalsFulfillmentTerm
                  horizonType={ContractProposalBaseSolutionHorizonType.mid_term}
                  goals={goals?.filter((item) => item.horizon === GoalOverviewHorizon.middle)}
                  contractProposals={getContractProposalsByHorizon(ContractProposalBaseSolutionHorizonType.mid_term)}
                  currentContracts={getCurrentContractsByHorizon(CurrentContractBaseSolutionHorizonType.mid_term)}
                  futureAmount={getFutureAmountByHorizon(ContractProposalBaseSolutionHorizonType.mid_term)}
                  handleCurrentSolutionDrawerOpen={handleCurrentSolutionDrawerOpen}
                  handleProposedSolutionDrawerOpen={handleProposedSolutionDrawerOpen}
                  nsurePlan={getNsurePlanByHorizon(ContractProposalBaseSolutionHorizonType.mid_term)}
                />

                <GoalsFulfillmentTerm
                  horizonType={ContractProposalBaseSolutionHorizonType.long_term}
                  goals={goals?.filter((item) => item.horizon === GoalOverviewHorizon.long)}
                  contractProposals={getContractProposalsByHorizon(ContractProposalBaseSolutionHorizonType.long_term)}
                  currentContracts={getCurrentContractsByHorizon(CurrentContractBaseSolutionHorizonType.long_term)}
                  futureAmount={getFutureAmountByHorizon(ContractProposalBaseSolutionHorizonType.long_term)}
                  handleCurrentSolutionDrawerOpen={handleCurrentSolutionDrawerOpen}
                  handleProposedSolutionDrawerOpen={handleProposedSolutionDrawerOpen}
                  nsurePlan={getNsurePlanByHorizon(ContractProposalBaseSolutionHorizonType.long_term)}
                />
              </Box>
            ) : (
              <Loader />
            )}
          </Stack>
          <Divider orientation="vertical" flexItem />
          <GoalsFulfillmentSidebarWrapper
            futureAmount={
              (contractProposals?.summary.all.futureAmount.value ?? 0) + (currentContracts?.summary.all.futureAmount.value ?? 0)
            }
          />
        </Stack>

        <CurrentSolutionDrawer
          horizonType={currentSolutionHorizonType}
          open={currentSolutionDrawerOpen}
          onClose={handleCurrentSolutionDrawerClose}
          investmentToolType={ContractProposalBaseInvestmentToolType.investment}
        />

        <ProposedSolutionDrawer
          nsurePlan={getNsurePlanByHorizon(proposedSolutionHorizonType)}
          open={proposedSolutionDrawerOpen}
          onClose={handleProposedSolutionDrawerClose}
          investmentToolType={ContractProposalBaseInvestmentToolType.investment}
          horizonType={proposedSolutionHorizonType}
        />
      </GoalsFulfillmentProvider>
    </InvestmentCaseContext.Provider>
  );
};
